import { notification } from '@/components/Notification';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { PosUIConfig } from '@/models/pos';
import { assertUnreachable } from '@/utils/common';
import { loadSoundResources, playSound } from '@/utils/sound';
import { STORAGE_KEY_POS_UI_CONFIG } from '@/utils/storage';
import { Layout, Space } from 'antd';
import React, { ReactNode, Reducer, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import {
  addWsListener,
  CommonTextTipMsg,
  DOMAIN_INTERACT_OUTSIDE,
  DOMAIN_POS,
  removeWsListener,
  WsActions,
} from '../../api/ws';
import { DEFAULT_POS_UI_CONFIG } from '../../pages/POS/Pages/System/UI';
import PosButton from '../PosButton';
import LayoutPosHeader from './Header';
import styles from './style.less';
import Synchronizer from './Synchronizer';
import { usePosHotkeys } from './usePosHotkeys';
import KickAwayModal from './Synchronizer/kickAwayModal';

const { Content } = Layout;

type LayoutProps = {
  children: ReactNode;
};

type POSConfig = {
  uiConfig: PosUIConfig;
};

type POSConfigActions = {
  type: 'UPDATE_UI';
  payload: PosUIConfig;
};

export const POSConfigContext = React.createContext<{
  config: POSConfig;
  updateConfig: React.Dispatch<POSConfigActions>;
}>(null);

enum RequireLoginMode {
  Never,
  Required,
  RequiredAdminChanged,
}

const LayoutPos: React.FC<LayoutProps> = ({ children }: LayoutProps): React.ReactElement => {
  const [, , helpers] = useContext(BaseInfoContext);
  const [notificationNode] = useState<ReactNode>(null);
  // notification key 记录，防止重复弹出
  const notificationKeys = useRef<Set<string>>(new Set());
  const [posUIConfig] = useLocalStorage<PosUIConfig>(STORAGE_KEY_POS_UI_CONFIG, DEFAULT_POS_UI_CONFIG);

  // 加载声音资源
  useEffect(() => {
    loadSoundResources();
    document.body.classList.add('posContainer');
  }, []);

  // pos config reducer
  const [posConfig, updatePosConfig] = useReducer<Reducer<POSConfig, POSConfigActions>>(
    (state, action) => {
      switch (action.type) {
        case 'UPDATE_UI':
          return {
            ...state,
            uiConfig: { ...action.payload },
          };
      }
      return assertUnreachable(action.type);
    },
    {
      uiConfig: posUIConfig,
    }
  );

  const posConfigCtxValue = useMemo(() => ({
    config: posConfig,
    updateConfig: updatePosConfig,
  }), [posConfig, updatePosConfig]);

  useEffect(() => {
    const id = addWsListener<CommonTextTipMsg>(DOMAIN_POS, WsActions.COMMON_TEXT_TIP, data => {
      notification[data.level]({
        duration: null,
        message: `远程通知`,
        description: data.text,
        key: data.key,
        actions: (
          <>
            <PosButton
              type={'primary'}
              onClick={() => {
                notification.destroy(data.key);
              }}>
              知道了
            </PosButton>
          </>
        ),
      });
    });

    const id2 = addWsListener<{ username: string; displayName: string; memo: string }>(
      DOMAIN_INTERACT_OUTSIDE,
      WsActions.WEAPP_USER_VISIT_IN_POI,
      payload => {
        if (!payload || !payload.username) return;
        const key = `global-notification-user-visit-weapp-${payload.username}`;
        if (notificationKeys.current.has(key)) {
          // 防止同一个用户的重复通知
          return;
        }
        notificationKeys.current.add(key);
        playSound('notification');
        notification.info({
          onClose: () => {
            notificationKeys.current.delete(key);
          },
          duration: null,
          message: `查看客户`,
          description: (
            <>
              <div>客户: {payload.displayName}</div>
              <div>事件: {payload.memo}</div>
            </>
          ),
          key,
          actions: (
            <Space>
              <PosButton
                size={'large'}
                onClick={() => {
                  notificationKeys.current.delete(key);
                  notification.destroy(key);
                }}>
                忽略
              </PosButton>
              <PosButton
                size={'large'}
                type={'primary'}
                onClick={() => {
                  notificationKeys.current.delete(key);
                  notification.destroy(key);
                  helpers.showUserModal({
                    userName: payload.username,
                  });
                }}>
                查看
              </PosButton>
            </Space>
          ),
        });
      }
    );
    return () => {
      removeWsListener(id);
      removeWsListener(id2);
    };
  }, []);

  // 快捷键
  usePosHotkeys();

  return (
    <>
      <KickAwayModal />
      <Layout className={styles.content}>
        <LayoutPosHeader />
        <POSConfigContext.Provider
          value={posConfigCtxValue}>
          <Synchronizer>
            {notificationNode}
            <Content className={styles.children}>{children}</Content>
          </Synchronizer>
        </POSConfigContext.Provider>
        {/* </ConfigProvider> */}
      </Layout>
    </>
  );
};

export default LayoutPos;
